import { AnimatePresence, motion } from "framer-motion";
import useMeasure from "util/useMeasure";

interface Props {
  ready: boolean;
  theme: keyof typeof theming;
}

export default function CatLoader({ ready, theme: themeKey }: Props) {
  const duration = 3;
  const [{ width }, ref] = useMeasure<HTMLDivElement>();
  const theme = theming[themeKey];

  return (
    <div className="aspect-h-1 aspect-w-1 relative">
      <div className="absolute inset-0" ref={ref}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 50 52"
          width="100%"
        >
          <motion.path
            stroke={theme[500]}
            fill="none"
            d="M1 32c0 9.5 15 19 24 19 7.5 0 24-9 24-20 0-12-5.5-13-5.5-14.5-1.5-8-2.5-14.471-4-15-1.5-.529-9 11.5-10.5 14-.12.198-8.835.255-9 0C14.5 7 11 2 9.5 1.5c-2 1-4 16.5-4 16.5S1 19.5 1 32Z"
            key={ready ? "ready" : "loading"}
            transition={{
              duration: duration,
              delay: 0,
              repeat: ready ? 0 : Infinity,
              type: "spring",
              bounce: 0,
            }}
            variants={{
              ready: {
                pathLength: 1.01,
                stroke: white,
                fill: theme[500],
              },
            }}
            animate={
              ready
                ? "ready"
                : {
                    pathLength: 1.01,
                    fill: theme.transparent,
                  }
            }
            initial={{
              pathLength: 0,
              fill: theme.transparent,
            }}
            exit={{
              pathLength: 0,
              fill: theme.transparent,
            }}
          />
        </svg>

        <motion.div
          className="absolute left-0 right-0 top-1/2 translate-y-1/3 text-center font-brik"
          style={{ fontSize: `${width / 10}px`, color: theme[500] }}
          animate={ready ? "ready" : width > 0 ? "visible" : "hidden"}
          variants={{
            visible: {
              color: theme[500],
              opacity: 1,
              y: 0,
            },
            hidden: {
              color: theme[100],
              opacity: 0,
              y: -10,
            },
            ready: {
              color: theme.textReady,
              opacity: 1,
              y: 0,
            },
          }}
        >
          <AnimatePresence>
            <div className="relative inline-block">
              {ready ? (
                <motion.div
                  key={"ready"}
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -50, opacity: 0 }}
                >
                  Ready!
                </motion.div>
              ) : (
                <motion.div
                  key="loading"
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -50, opacity: 0 }}
                >
                  Loading
                </motion.div>
              )}

              {!ready && (
                <span
                  key="ellipsis"
                  className="absolute left-full top-0 translate-y-[20%] leading-none"
                >
                  ...
                </span>
              )}
            </div>
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}

const white = "#fff";
const black = "#000";
const theming = {
  green: {
    500: "rgb(16, 185, 129)",
    100: "rgb(209, 250, 229)",
    transparent: "rgba(16, 185, 129, 0)",
    textReady: white,
  },

  purple: {
    500: "rgb(168, 85, 247)",
    100: "rgb(243, 232, 255)",
    transparent: "rgba(168, 85, 247, 0)",
    textReady: white,
  },

  white: {
    500: "rgba(255, 255, 255, 0.85)",
    100: "rgb(255, 255, 255)",
    transparent: "rgba(255, 255, 255, 0)",
    textReady: "rgb(16, 185, 129)",
  },
};
